import React, { useMemo, useState, useCallback } from 'react';
import { Button, Card, Col, Input, Modal, Row, Space, Switch, Typography, message, Segmented, List } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import './AIModelBuilderLessonAdmin.css';
import JsonEditor from '../../JsonEditor/JsonEditor';
import { TAIModelBuilderDatasetEntry, TAIModelBuilderDataTypeEntry, TAIModelBuilderLessonConfig } from '../../../models/courses/aiModelBuilder';
import { useObjectsApi } from '../../../api/objects/objects';
import type { TImage, TSound } from '../../../models/objects/media';
import { getImagePath, getSoundPath } from '../../../utils/format/valueFormat';


interface AIModelBuilderLessonAdminProps {
  config: TAIModelBuilderLessonConfig;
  onConfigChange: (config: TAIModelBuilderLessonConfig) => void;
  onSave: () => void;
  saving: boolean;
  initialStatement?: string;
  onStatementChange?: (statement: string) => void;
}

const LANGUAGES: Array<'en' | 'fr' | 'ar'> = ['en', 'fr', 'ar'];

const AIModelBuilderLessonAdmin: React.FC<AIModelBuilderLessonAdminProps> = ({
  config,
  onConfigChange,
  onSave,
  saving,
  initialStatement,
  onStatementChange,
}) => {
  const { t } = useTranslation();
  const [newEntryKey, setNewEntryKey] = useState('');
  const [newDataTypeKey, setNewDataTypeKey] = useState('');
  const [jsonEditorOpen, setJsonEditorOpen] = useState(false);
  const [featureInputs, setFeatureInputs] = useState<Record<string, Record<'en' | 'fr' | 'ar', string>>>({});
  const [noiseInputs, setNoiseInputs] = useState<Record<'en' | 'fr' | 'ar', string>>({ en: '', fr: '', ar: '' });
  const [keyTypeByEntry, setKeyTypeByEntry] = useState<Record<string, 'text' | 'image' | 'audio'>>({});

  // Media picker state
  const objectsApi = useObjectsApi();
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaPickerType, setMediaPickerType] = useState<'image' | 'audio'>('image');
  const [mediaPickerTargetKeyId, setMediaPickerTargetKeyId] = useState<string | null>(null);
  const [mediaPickerContext, setMediaPickerContext] = useState<'key' | 'feature' | 'noise'>('key');
  const [mediaFeatureTargetLang, setMediaFeatureTargetLang] = useState<'en' | 'fr' | 'ar' | null>(null);
  const [mediaNoiseTargetLang, setMediaNoiseTargetLang] = useState<'en' | 'fr' | 'ar' | null>(null);
  const [images, setImages] = useState<TImage[]>([]);
  const [sounds, setSounds] = useState<TSound[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaSearch, setMediaSearch] = useState('');
  const filteredImages = useMemo(() => {
    const q = mediaSearch.trim().toLowerCase();
    if (!q) return images;
    return images.filter((img) => (img.file_name || '').toLowerCase().includes(q) || (img.file_path || '').toLowerCase().includes(q));
  }, [images, mediaSearch]);
  const filteredSounds = useMemo(() => {
    const q = mediaSearch.trim().toLowerCase();
    if (!q) return sounds;
    return sounds.filter((s) => (s.file_name || '').toLowerCase().includes(q) || (s.file_path || '').toLowerCase().includes(q));
  }, [sounds, mediaSearch]);

  const detectKeyType = useCallback((value: string | undefined): 'text' | 'image' | 'audio' => {
    if (!value) return 'text';
    const v = value.toLowerCase();
    if (/(\.png|\.jpe?g|\.gif|\.svg|\.webp)$/.test(v) || v.includes('/images/')) return 'image';
    if (/(\.mp3|\.wav|\.ogg|\.m4a)$/.test(v) || v.includes('/sounds/') || v.includes('/audio/')) return 'audio';
    return 'text';
  }, []);

  const handleKeyTypeChange = (keyId: string, type: 'text' | 'image' | 'audio') => {
    setKeyTypeByEntry((prev) => ({ ...prev, [keyId]: type }));
  };

  const openMediaPicker = async (
    keyId: string,
    type: 'image' | 'audio',
    context: 'key' | 'feature' | 'noise' = 'key',
    lang?: 'en' | 'fr' | 'ar',
  ) => {
    setMediaPickerTargetKeyId(keyId);
    setMediaPickerType(type);
    setMediaPickerContext(context);
    setMediaFeatureTargetLang(context === 'feature' ? (lang ?? null) : null);
    setMediaNoiseTargetLang(context === 'noise' ? (lang ?? null) : null);
    setMediaPickerOpen(true);
    setMediaLoading(true);
    try {
      if (type === 'image') {
        const resp = await objectsApi.getImages();
        setImages(resp.data || []);
      } else {
        const resp = await objectsApi.getSounds();
        setSounds(resp.data || []);
      }
    } catch (e: any) {
      message.error(e?.response?.data || e?.message || 'Failed to load media');
    } finally {
      setMediaLoading(false);
    }
  };

  const chooseMedia = (path: string) => {
    if (mediaPickerContext !== 'noise' && !mediaPickerTargetKeyId) return;
    if (mediaPickerContext === 'key') {
      updateConfig((current) => ({
        ...current,
        dataset: {
          ...current.dataset,
          [mediaPickerTargetKeyId!]: {
            ...current.dataset[mediaPickerTargetKeyId!],
            key: path,
          },
        },
      }));
      setKeyTypeByEntry((prev) => ({ ...prev, [mediaPickerTargetKeyId!]: mediaPickerType }));
    } else if (mediaPickerContext === 'feature' && mediaFeatureTargetLang) {
      const fKey = `features_${mediaFeatureTargetLang}` as keyof TAIModelBuilderDatasetEntry;
      updateConfig((current) => {
        const existing = current.dataset[mediaPickerTargetKeyId!];
        const updatedFeatures = ([...(existing[fKey] as string[] | undefined || []), path]);
        return {
          ...current,
          dataset: {
            ...current.dataset,
            [mediaPickerTargetKeyId!]: {
              ...existing,
              [fKey]: updatedFeatures,
            },
          },
        } as TAIModelBuilderLessonConfig;
      });
    } else if (mediaPickerContext === 'noise' && mediaNoiseTargetLang) {
      updateConfig((current) => {
        const bruitKey = `bruit_${mediaNoiseTargetLang}` as keyof TAIModelBuilderLessonConfig;
        const existing = (current[bruitKey] as string[] | undefined) || [];
        return {
          ...current,
          [bruitKey]: [...existing, path],
        } as TAIModelBuilderLessonConfig;
      });
    }
    setMediaPickerOpen(false);
    setMediaPickerTargetKeyId(null);
    setMediaFeatureTargetLang(null);
    setMediaNoiseTargetLang(null);
  };

  const updateConfig = (updater: (current: TAIModelBuilderLessonConfig) => TAIModelBuilderLessonConfig) => {
    onConfigChange(updater(config));
  };

  const handleAddEntry = () => {
    const trimmedKey = newEntryKey.trim();
    if (!trimmedKey) {
      message.warning(t('aiModelBuilder.admin.messages.keyRequired'));
      return;
    }
    if (config.dataset?.[trimmedKey]) {
      message.warning(t('aiModelBuilder.admin.messages.keyExists'));
      return;
    }
    updateConfig((current) => ({
      ...current,
      dataset: {
        ...current.dataset,
        [trimmedKey]: { key: trimmedKey },
      },
    }));
    setNewEntryKey('');
    message.success(t('aiModelBuilder.admin.messages.entryAdded'));
  };

  const handleDeleteEntry = (entryKey: string) => {
    updateConfig((current) => {
      const updated = { ...current.dataset };
      delete updated[entryKey];
      return { ...current, dataset: updated };
    });
  };

  const handleAddDataType = () => {
    const trimmedKey = newDataTypeKey.trim();
    if (!trimmedKey) {
      message.warning(t('aiModelBuilder.admin.messages.keyRequired'));
      return;
    }
    updateConfig((current) => ({
      ...current,
      data_type: {
        ...(current.data_type || {}),
        [trimmedKey]: { title_en: trimmedKey, status: 'true' },
      },
    }));
    setNewDataTypeKey('');
  };


  const renderFeatures = (entryKey: string, entry: TAIModelBuilderDatasetEntry, lang: 'en' | 'fr' | 'ar') => {
    const featureKey = `features_${lang}` as const;
    const features = (entry[featureKey] || []) as string[];
    const inputValue = featureInputs[entryKey]?.[lang] ?? '';

    const addFeature = () => {
      if (!inputValue.trim()) return;
      updateConfig((current) => {
        const existing = current.dataset[entryKey];
        const updatedFeatures = [
          ...(((existing[`features_${lang}` as keyof TAIModelBuilderDatasetEntry] as string[]) || [])),
          inputValue.trim(),
        ];
        return {
          ...current,
          dataset: {
            ...current.dataset,
            [entryKey]: {
              ...existing,
              [`features_${lang}`]: updatedFeatures,
            },
          },
        } as TAIModelBuilderLessonConfig;
      });
      setFeatureInputs((prev) => ({
        ...prev,
        [entryKey]: {
          ...(prev[entryKey] || {}),
          [lang]: '',
        },
      }));
    };

    const removeFeature = (index: number) => {
      updateConfig((current) => {
        const existing = current.dataset[entryKey];
        const updatedFeatures = (existing[`features_${lang}` as keyof TAIModelBuilderDatasetEntry] as string[] || []).filter(
          (_, i) => i !== index,
        );
        return {
          ...current,
          dataset: {
            ...current.dataset,
            [entryKey]: {
              ...existing,
              [`features_${lang}`]: updatedFeatures,
            },
          },
        } as TAIModelBuilderLessonConfig;
      });
    };

    return (
      <Card 
        size="small" 
        title={t('aiModelBuilder.admin.featuresTitle', { lang: lang.toUpperCase() })} 
        bordered={false}
        className="aimb-feature-card"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div className="aimb-chip-container">
            {features.length === 0 ? (
              <Typography.Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                No features yet
              </Typography.Text>
            ) : (
              features.map((feature, idx) => {
                const lower = (feature || '').toLowerCase();
                const isImg = /\.(png|jpe?g|gif|svg|webp)(\?|#|$)/.test(lower) || lower.includes('/images/') || lower.includes('/uploads/');
                const isAud = /\.(mp3|wav|ogg|m4a)(\?|#|$)/.test(lower) || lower.includes('/sounds/') || lower.includes('/audio/');
                return (
                  <span key={`${entryKey}-${lang}-${idx}`} className="aimb-chip">
                    {isImg ? (
                      <img src={getImagePath(feature)} alt="feature" style={{ maxHeight: 28, maxWidth: 64, objectFit: 'cover', borderRadius: 4 }} />
                    ) : isAud ? (
                      <audio src={getSoundPath(feature)} controls style={{ height: 28 }} />
                    ) : (
                      feature
                    )}
                    <DeleteOutlined onClick={() => removeFeature(idx)} />
                  </span>
                );
              })
            )}
          </div>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder={t('aiModelBuilder.admin.featurePlaceholder')}
              value={inputValue}
              onChange={(e) =>
                setFeatureInputs((prev) => ({
                  ...prev,
                  [entryKey]: {
                    ...(prev[entryKey] || {}),
                    [lang]: e.target.value,
                  },
                }))
              }
              onPressEnter={addFeature}
              size="small"
            />
            <Button icon={<PlusOutlined />} type="primary" onClick={addFeature} size="small" />
            <Button onClick={() => openMediaPicker(entryKey, 'image', 'feature', lang)} size="small">Image</Button>
            <Button onClick={() => openMediaPicker(entryKey, 'audio', 'feature', lang)} size="small">Audio</Button>
          </Space.Compact>
        </Space>
      </Card>
    );
  };

  const renderBruitSection = (lang: 'en' | 'fr' | 'ar') => {
    const list = (config[`bruit_${lang}` as keyof TAIModelBuilderLessonConfig] as string[] | undefined) || [];
    const value = noiseInputs[lang];

    const addBruit = () => {
      if (!value.trim()) return;
      updateConfig((current) => {
        const existing = (current[`bruit_${lang}` as keyof TAIModelBuilderLessonConfig] as string[] | undefined) || [];
        return {
          ...current,
          [`bruit_${lang}`]: [...existing, value.trim()],
        } as TAIModelBuilderLessonConfig;
      });
      setNoiseInputs((prev) => ({ ...prev, [lang]: '' }));
    };

    const removeBruit = (index: number) => {
      updateConfig((current) => {
        const existing = (current[`bruit_${lang}` as keyof TAIModelBuilderLessonConfig] as string[] | undefined) || [];
        return {
          ...current,
          [`bruit_${lang}`]: existing.filter((_, i) => i !== index),
        } as TAIModelBuilderLessonConfig;
      });
    };

    return (
      <Card 
        size="small" 
        title={t('aiModelBuilder.admin.bruitTitle', { lang: lang.toUpperCase() })} 
        bordered={false}
        className="aimb-bruit-card"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div className="aimb-chip-container">
            {list.length === 0 ? (
              <Typography.Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                No noise items yet
              </Typography.Text>
            ) : (
              list.map((item, idx) => {
                const lower = (item || '').toLowerCase();
                const isImg = /(\.png|\.jpe?g|\.gif|\.svg|\.webp)(\?|#|$)/.test(lower) || lower.includes('/images/') || lower.includes('/uploads/');
                const isAud = /(\.mp3|\.wav|\.ogg|\.m4a)(\?|#|$)/.test(lower) || lower.includes('/sounds/') || lower.includes('/audio/');
                return (
                  <span key={`${lang}-${idx}`} className="aimb-chip">
                    {isImg ? (
                      <img src={getImagePath(item)} alt="noise" style={{ maxHeight: 28, maxWidth: 64, objectFit: 'cover', borderRadius: 4 }} />
                    ) : isAud ? (
                      <audio src={getSoundPath(item)} controls style={{ height: 28 }} />
                    ) : (
                      item
                    )}
                    <DeleteOutlined onClick={() => removeBruit(idx)} />
                  </span>
                );
              })
            )}
          </div>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder={t('aiModelBuilder.admin.bruitPlaceholder')}
              value={value}
              onChange={(e) => setNoiseInputs((prev) => ({ ...prev, [lang]: e.target.value }))}
              onPressEnter={addBruit}
              size="small"
            />
            <Button icon={<PlusOutlined />} type="primary" onClick={addBruit} size="small" />
            <Button onClick={() => openMediaPicker('noise', 'image', 'noise', lang)} size="small">Image</Button>
            <Button onClick={() => openMediaPicker('noise', 'audio', 'noise', lang)} size="small">Audio</Button>
          </Space.Compact>
        </Space>
      </Card>
    );
  };

  const renderDataTypeCard = (keyId: string, entry: TAIModelBuilderDataTypeEntry) => (
    <Card
      key={keyId}
      size="small"
      className="aimb-item-card"
      title={entry.title_en || keyId}
      extra={<DeleteOutlined onClick={() => handleDeleteDataType(keyId)} />}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {LANGUAGES.map((lang) => (
          <Input
            key={`${keyId}-${lang}`}
            value={entry[`title_${lang}` as keyof TAIModelBuilderDataTypeEntry] as string | undefined}
            placeholder={t('aiModelBuilder.admin.dataTypePlaceholder', { lang: lang.toUpperCase() })}
            onChange={(e) =>
              updateConfig((current) => ({
                ...current,
                data_type: {
                  ...(current.data_type || {}),
                  [keyId]: {
                    ...((current.data_type || {})[keyId] || {}),
                    [`title_${lang}`]: e.target.value,
                  },
                },
              }))
            }
          />
        ))}
        <Space>
          <Switch
            checked={(entry.status ?? 'false') === 'true'}
            onChange={(checked) =>
              updateConfig((current) => ({
                ...current,
                data_type: {
                  ...(current.data_type || {}),
                  [keyId]: {
                    ...((current.data_type || {})[keyId] || {}),
                    status: checked ? 'true' : 'false',
                  },
                },
              }))
            }
          />
          <span>{t('aiModelBuilder.admin.active')}</span>
        </Space>
      </Space>
    </Card>
  );


  const handleDeleteDataType = (keyId: string) => {
    updateConfig((current) => {
      const updated = { ...(current.data_type || {}) };
      delete updated[keyId];
      return { ...current, data_type: updated };
    });
  };


  const datasetEntries = useMemo(() => Object.entries(config.dataset || {}), [config.dataset]);
  const dataTypeEntries = useMemo(() => Object.entries(config.data_type || {}), [config.data_type]);

  return (
    <div className="aimb-admin-container">
      <Card className="aimb-admin-header-card">
        <div className="aimb-admin-header">
          <div className="aimb-admin-header-content">
            <Typography.Title level={3} className="aimb-admin-title">
              {t('aiModelBuilder.admin.title')}
            </Typography.Title>
            <Typography.Paragraph type="secondary" className="aimb-admin-description">
              {t('aiModelBuilder.admin.description')}
            </Typography.Paragraph>
          </div>
          <Space size="middle">
            <Button icon={<EditOutlined />} onClick={() => setJsonEditorOpen(true)}>
              {t('aiModelBuilder.admin.openJsonEditor')}
            </Button>
            <Button type="primary" size="large" onClick={onSave} loading={saving}>
              {t('aiModelBuilder.admin.save')}
            </Button>
          </Space>
        </div>
      </Card>

      <Card className="aimb-section-card" title={t('aiModelBuilder.admin.statement')}>
        <Input.TextArea
          rows={4}
          value={initialStatement || config.statement || ''}
          onChange={(e) => {
            onStatementChange?.(e.target.value);
            updateConfig((current) => ({ ...current, statement: e.target.value }));
          }}
          placeholder={t('aiModelBuilder.admin.statementPlaceholder')}
          style={{ fontSize: '14px' }}
        />
      </Card>

      <Card className="aimb-section-card" title={t('aiModelBuilder.admin.bruitTitleGlobal')}>
        <Row gutter={[16, 16]}>
          {LANGUAGES.map((lang) => (
            <Col span={8} key={lang}>
              {renderBruitSection(lang)}
            </Col>
          ))}
        </Row>
      </Card>

      <Card className="aimb-section-card" title={t('aiModelBuilder.admin.datasetTitle')} extra={
        <Space.Compact>
          <Input
            placeholder={t('aiModelBuilder.admin.newKeyPlaceholder')}
            value={newEntryKey}
            onChange={(e) => setNewEntryKey(e.target.value)}
          />
          <Button icon={<PlusOutlined />} type="primary" onClick={handleAddEntry}>
            {t('aiModelBuilder.admin.addEntry')}
          </Button>
        </Space.Compact>
      }>
        {!datasetEntries.length && (
          <div className="aimb-empty-state">
            <Typography.Text type="secondary">{t('aiModelBuilder.admin.noEntries')}</Typography.Text>
          </div>
        )}
        <Row gutter={[16, 16]}>
          {datasetEntries.map(([keyId, entry]) => (
            <Col span={12} key={keyId}>
              <Card
                title={entry.key || keyId}
                extra={<DeleteOutlined onClick={() => handleDeleteEntry(keyId)} />}
                className="aimb-item-card"
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <Segmented
                      value={keyTypeByEntry[keyId] || detectKeyType(entry.key)}
                      onChange={(val) => handleKeyTypeChange(keyId, val as any)}
                      options={[
                        { label: 'Texte', value: 'text' },
                        { label: 'Image', value: 'image' },
                        { label: 'Audio', value: 'audio' },
                      ]}
                    />

                    {(() => {
                      const type = keyTypeByEntry[keyId] || detectKeyType(entry.key);
                      if (type === 'text') {
                        return (
                          <Input
                            value={entry.key}
                            onChange={(e) =>
                              updateConfig((current) => ({
                                ...current,
                                dataset: {
                                  ...current.dataset,
                                  [keyId]: {
                                    ...current.dataset[keyId],
                                    key: e.target.value,
                                  },
                                },
                              }))
                            }
                            placeholder={t('aiModelBuilder.admin.keyPlaceholder')}
                          />
                        );
                      }
                      return (
                        <Space.Compact style={{ width: '100%' }}>
                          <Input
                            value={entry.key}
                            readOnly
                            placeholder={type === 'image' ? 'Chemin image' : 'Chemin audio'}
                          />
                          <Button onClick={() => openMediaPicker(keyId, type)}>
                            {type === 'image' ? 'Sélectionner image' : 'Sélectionner audio'}
                          </Button>
                          {entry.key && (
                            <Button onClick={() =>
                              updateConfig((current) => ({
                                ...current,
                                dataset: {
                                  ...current.dataset,
                                  [keyId]: { ...current.dataset[keyId], key: '' },
                                },
                              }))
                            }>Effacer</Button>
                          )}
                        </Space.Compact>
                      );
                    })()}
                  </Space>

                  {(() => {
                    const type = keyTypeByEntry[keyId] || detectKeyType(entry.key);
                    if (type === 'image' && entry.key) {
                      return (
                        <div className="aimb-chip-container" style={{ paddingTop: 8 }}>
                          <img src={entry.key} alt="Preview" style={{ maxWidth: '100%', borderRadius: 4 }} />
                        </div>
                      );
                    }
                    if (type === 'audio' && entry.key) {
                      return (
                        <div className="aimb-chip-container" style={{ paddingTop: 8 }}>
                          <audio src={entry.key} controls />
                        </div>
                      );
                    }
                    return null;
                  })()}
                  <Row gutter={[12, 12]}>
                    {LANGUAGES.map((lang) => (
                      <Col span={8} key={`${keyId}-${lang}`}>
                        {renderFeatures(keyId, entry, lang)}
                      </Col>
                    ))}
                  </Row>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      <Card
        className="aimb-section-card"
        title={t('aiModelBuilder.admin.dataTypeTitle')}
        extra={
          <Space.Compact>
            <Input
              placeholder={t('aiModelBuilder.admin.newDataTypePlaceholder')}
              value={newDataTypeKey}
              onChange={(e) => setNewDataTypeKey(e.target.value)}
            />
            <Button icon={<PlusOutlined />} type="primary" onClick={handleAddDataType}>
              {t('aiModelBuilder.admin.add')}
            </Button>
          </Space.Compact>
        }
      >
        {!dataTypeEntries.length && (
          <div className="aimb-empty-state">
            <Typography.Text type="secondary">{t('aiModelBuilder.admin.noEntries')}</Typography.Text>
          </div>
        )}
        <Space direction="vertical" style={{ width: '100%' }}>
          {dataTypeEntries.map(([keyId, entry]) => renderDataTypeCard(keyId, entry))}
        </Space>
      </Card>

      <Modal
        title={t('aiModelBuilder.admin.editorTitle')}
        open={jsonEditorOpen}
        onCancel={() => setJsonEditorOpen(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <JsonEditor<TAIModelBuilderLessonConfig>
          value={config}
          initialText={JSON.stringify(config, null, 2)}
          title={t('aiModelBuilder.admin.editorTitle')}
          onApply={(updated) => {
            onConfigChange(updated);
            onStatementChange?.(updated.statement || initialStatement || '');
            setJsonEditorOpen(false);
            message.success(t('aiModelBuilder.admin.messages.jsonApplied'));
          }}
        />
      </Modal>

      <Modal
        title={mediaPickerType === 'image' ? 'Sélectionner une image' : 'Sélectionner un audio'}
        open={mediaPickerOpen}
        onCancel={() => setMediaPickerOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
        >
          {mediaLoading ? (
            <Typography.Text>Chargement...</Typography.Text>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Input.Search
                placeholder={t('Rechercher par nom de fichier') as any}
                allowClear
                value={mediaSearch}
                onChange={(e) => setMediaSearch(e.target.value)}
              />
              {mediaPickerType === 'image' ? (
                <List
                  dataSource={filteredImages}
                  rowKey={(item) => String(item.id)}
                  renderItem={(item) => (
                    <List.Item onClick={() => chooseMedia(item.file_path)} style={{ cursor: 'pointer' }}>
                      <Space>
                        <img src={item.file_path} alt={item.file_name} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4 }} />
                        <span>{item.file_name}</span>
                      </Space>
                    </List.Item>
                  )}
                />
              ) : (
                <List
                  dataSource={filteredSounds}
                  rowKey={(item) => String(item.id)}
                  renderItem={(item) => (
                    <List.Item onClick={() => chooseMedia(item.file_path)} style={{ cursor: 'pointer' }}>
                      <Space>
                        <span>{item.file_name}</span>
                      </Space>
                    </List.Item>
                  )}
                />
              )}
            </Space>
          )}
        </Modal>
    </div>
  );
};

export default AIModelBuilderLessonAdmin;
